// src/BeneficiaryForm.js
//
// NEW CONCEPT: Beneficiary.activities is a MANY-TO-MANY field - one
// beneficiary can be linked to SEVERAL activities at once. HTML's
// <select multiple> lets the user pick more than one option (usually by
// Ctrl/Cmd-clicking), and we read out ALL selected values, not just one.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function BeneficiaryForm({ token, beneficiary, onSaved, onCancel }) {
  const [activityOptions, setActivityOptions] = useState([]);

  const [fullName, setFullName] = useState(beneficiary?.full_name || '');
  const [gender, setGender] = useState(beneficiary?.gender || '');
  const [dob, setDob] = useState(beneficiary?.date_of_birth || '');
  const [location, setLocation] = useState(beneficiary?.location || '');
  const [contactNumber, setContactNumber] = useState(beneficiary?.contact_number || '');
  // activities holds an ARRAY of selected activity IDs, e.g. [2, 5] -
  // note the [] default, since even zero selections should be a valid
  // empty array, not undefined.
  const [activities, setActivities] = useState(beneficiary?.activities || []);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/activities/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setActivityOptions(data);
      });
  }, [token]);

  // Special handler for the multi-select: e.target.selectedOptions is a
  // browser-native list of every currently-highlighted <option>, which
  // we convert into a plain array of just their values (the IDs).
  const handleActivitiesChange = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setActivities(selectedIds);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      full_name: fullName,
      gender,
      date_of_birth: dob || null,
      location,
      contact_number: contactNumber,
      activities,
    };

    const url = beneficiary
      ? `${API_BASE_URL}/api/beneficiaries/${beneficiary.id}/`
      : `${API_BASE_URL}/api/beneficiaries/`;
    const method = beneficiary ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{beneficiary ? 'Edit Beneficiary' : 'New Beneficiary'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
        <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
        <select value={gender} onChange={(e) => setGender(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Not specified</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
        <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
        <input type="text" value={contactNumber} onChange={(e) => setContactNumber(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Activities (Ctrl/Cmd-click to select multiple)</label>
        <select multiple value={activities} onChange={handleActivitiesChange}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 h-32 focus:outline-none focus:ring-2 focus:ring-blue-400">
          {activityOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default BeneficiaryForm;
